import type { BoardDto } from '@kanban/shared';
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { usePatchBoard, useUsers } from '../lib/boards.js';
import { useCurrentUser } from '../lib/session.js';
import { useSheetDrag } from '../lib/sheet-drag.js';
import { useToast } from '../lib/toast.js';
import { Avatar } from './Avatar.js';

/**
 * Board-Einstellungen: Name, Symbol, Teilnehmer, Löschen.
 *
 * Der Endpunkt dafuer stand seit Phase 1 (`PATCH /boards/:id`), es gab nur
 * keinen Weg dorthin — ein Board liess sich anlegen und danach nie wieder
 * aendern. Hier ist der Weg.
 */
export function BoardSettingsSheet({ board, onClose }: { board: BoardDto; onClose: () => void }) {
  const me = useCurrentUser();
  const users = useUsers();
  const patch = usePatchBoard(board.id);
  const drag = useSheetDrag(onClose);
  const navigate = useNavigate();
  const toast = useToast();

  const [title, setTitle] = useState(board.title);
  const [icon, setIcon] = useState(board.icon ?? '');
  const [memberIds, setMemberIds] = useState<string[]>(board.members.map((member) => member.id));
  /*
   * Loeschen ist ueber die Oberflaeche nicht rueckgaengig zu machen — anders
   * als beim Archivieren einer Karte gibt es keinen Rueckgaengig-Toast.
   * Deshalb zweistufig statt eines einzelnen Klicks: der erste Klick
   * bestaetigt nur, der zweite loest aus.
   */
  const [loeschenBestaetigt, setLoeschenBestaetigt] = useState(false);

  /*
   * Teilnehmer darf nur ein Administrator aendern — das ist die Regel des
   * Servers (routes/boards.ts), nicht meine. Gesperrt statt versteckt: wer
   * nicht darf, soll trotzdem sehen, wer im Board ist.
   */
  const darfMitglieder = me.role === 'admin';

  /*
   * Eigentuemer bleiben drin. Der Server entfernt sie ohnehin nie
   * (`deleteMany … role: { not: 'owner' }`), aber ein Haekchen, das sich
   * abwaehlen laesst und danach doch gesetzt bleibt, waere eine Luege — und
   * die Reihenfolge der Mitglieder verraet nicht, wer Eigentuemer ist.
   * Deshalb traegt das DTO `ownerIds`.
   */
  const ownerIds = new Set(board.ownerIds);

  function toggle(userId: string) {
    if (!darfMitglieder || ownerIds.has(userId)) return;
    setMemberIds((current) =>
      current.includes(userId) ? current.filter((id) => id !== userId) : [...current, userId],
    );
  }

  function speichern() {
    const änderung: { title?: string; icon?: string | null; memberIds?: string[] } = {};
    if (title.trim() && title.trim() !== board.title) änderung.title = title.trim();

    const neuesIcon = icon.trim() || null;
    if (neuesIcon !== board.icon) änderung.icon = neuesIcon;

    if (darfMitglieder) {
      const vorher = [...board.members.map((member) => member.id)].sort();
      const nachher = [...memberIds].sort();
      if (vorher.join() !== nachher.join()) änderung.memberIds = memberIds;
    }

    if (Object.keys(änderung).length === 0) {
      onClose();
      return;
    }
    patch.mutate({ patch: änderung, updatedAt: board.updatedAt }, { onSuccess: onClose });
  }

  function löschen() {
    if (!loeschenBestaetigt) {
      setLoeschenBestaetigt(true);
      return;
    }
    patch.mutate(
      { patch: { archived: true }, updatedAt: board.updatedAt },
      {
        onSuccess: () => {
          toast('Board gelöscht', 'success');
          navigate('/');
        },
      },
    );
  }

  return (
    <div
      className="fixed inset-0 z-40 flex items-end justify-center bg-[rgb(0_0_0/45%)] md:items-center"
      role="dialog"
      aria-modal="true"
      aria-label="Board-Einstellungen"
      data-testid="board-settings"
      onClick={(event) => {
        if (event.target === event.currentTarget) onClose();
      }}
    >
      <div
        ref={drag.sheetRef}
        className="flex max-h-[85vh] w-full flex-col overflow-y-auto rounded-t-[var(--radius-lg)] bg-surface shadow-[var(--shadow-3)] md:max-w-md md:rounded-[var(--radius-lg)]"
      >
        {/* Derselbe Griff wie am Kartendetail — und er tut dasselbe. */}
        <div
          {...drag.handleProps}
          data-testid="sheet-handle"
          className="sticky top-0 z-10 flex cursor-grab justify-center bg-surface pt-2 pb-2 md:hidden"
        >
          <span className="h-1 w-10 rounded-full bg-border-strong" aria-hidden="true" />
        </div>

        <div className="flex items-center justify-between px-4 pb-2 md:pt-3">
          <h2 className="text-sm font-semibold">Board-Einstellungen</h2>
          <button
            type="button"
            onClick={onClose}
            aria-label="Einstellungen schließen"
            className="text-sm text-muted"
            style={{ minWidth: 'var(--touch-target)', minHeight: 'var(--touch-target)' }}
          >
            ✕
          </button>
        </div>

        <div className="px-4 pb-4">
          <label className="block text-xs font-medium text-muted" htmlFor="board-title">
            Name
          </label>
          <input
            id="board-title"
            value={title}
            onChange={(event) => setTitle(event.target.value)}
            maxLength={80}
            className="mt-1 w-full rounded-[var(--radius)] border border-border bg-surface px-3 py-2 text-base font-medium"
          />

          {/*
            Das Symbol stand seit Phase 1 im Datenmodell und in der
            Board-Liste — nur setzen konnte man es nirgends.
          */}
          <label className="mt-4 block text-xs font-medium text-muted" htmlFor="board-icon">
            Symbol (optional)
          </label>
          <input
            id="board-icon"
            value={icon}
            onChange={(event) => setIcon(event.target.value)}
            maxLength={8}
            placeholder="🏡"
            className="mt-1 w-24 rounded-[var(--radius)] border border-border bg-surface px-3 py-2 text-center text-lg"
          />

          <fieldset className="mt-5" disabled={!darfMitglieder}>
            <legend className="text-xs font-medium text-muted">Teilnehmer</legend>
            {!darfMitglieder && (
              <p className="mt-1 text-xs text-muted">
                Wer im Board ist, ändert ein Administrator.
              </p>
            )}

            <ul className="mt-2 space-y-1" data-testid="board-members">
              {(users.data ?? []).map((person) => {
                const drin = memberIds.includes(person.id);
                const istEigentuemer = ownerIds.has(person.id);
                return (
                  <li key={person.id}>
                    <button
                      type="button"
                      data-testid="member-toggle"
                      aria-pressed={drin}
                      disabled={!darfMitglieder || istEigentuemer}
                      onClick={() => toggle(person.id)}
                      className={`flex w-full items-center gap-2 rounded-[var(--radius)] border px-3 text-left text-sm disabled:opacity-60 ${
                        drin ? 'border-accent bg-accent-soft' : 'border-border'
                      }`}
                      style={{ minHeight: 'var(--touch-target)' }}
                    >
                      <Avatar name={person.name} color={person.avatarColor} />
                      <span className="flex-1">{person.name}</span>
                      {istEigentuemer ? (
                        <span className="text-xs text-muted">Eigentümer</span>
                      ) : (
                        <span aria-hidden="true" className="text-sm">
                          {drin ? '✓' : ''}
                        </span>
                      )}
                    </button>
                  </li>
                );
              })}
            </ul>
          </fieldset>

          <button
            type="button"
            data-testid="save-board"
            onClick={speichern}
            disabled={patch.isPending || title.trim().length === 0}
            className="mt-5 w-full rounded-[var(--radius)] bg-accent px-4 text-sm font-medium text-[var(--color-accent-contrast)] disabled:opacity-60"
            style={{ minHeight: 'var(--touch-target)' }}
          >
            Speichern
          </button>

          {darfMitglieder && (
            <div className="mt-6 border-t border-border pt-4">
              <p className="text-xs font-medium text-muted">Gefahrenzone</p>
              <button
                type="button"
                data-testid="delete-board"
                onClick={löschen}
                disabled={patch.isPending}
                className="mt-2 w-full rounded-[var(--radius)] border border-danger px-4 text-sm text-danger disabled:opacity-60"
                style={{ minHeight: 'var(--touch-target)' }}
              >
                {loeschenBestaetigt ? 'Wirklich löschen?' : 'Board löschen'}
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
